import {useState, useEffect} from 'react';
import SupplierInterchangeDialogWrapper from '../components/interchanges/supplier/dialog/SupplierInterchangeDialogWrapper';
import SecurSpaceContainer from '../components/common/SecurSpaceContainer';
import SupplierInterchangeMap from '../components/interchanges/supplier/SupplierInterchangeMap';
import SupplierInterchangeFilters from '../components/interchanges/supplier/filter/SupplierInterchangeFilters';
import { SupplierInterchangeSortByType } from '../components/interchanges/supplier/filter/SupplierInterchangeSortSelect';
import DownloadCSVButton from '../components/report/export/DownloadCSVButton';
import { SecurSpaceReportPaginationContainer } from '../components/common/reports/SecurSpaceReportPaginationContainer';
import { SecurSpaceReportContainer } from '../components/common/reports/SecurSpaceReportContainer';
import { ActionInterchange } from '../components/interchanges/data';
import { getSupplierInterchangesReport, editInterchange, confirmBookingNumber, moveInventory, deleteInterchange } from '../components/interchanges/requests/supplier-interchanges-requests';
import { useSnackbar } from '../context/snackbar-context';
import { getErrorMessageForNonStandardAndStandardResponse } from '../util/NetworkErrorUtil';
import { useBindSearchParam } from '../hooks/useBindSearchParam';
import { useSupplierInterchanges } from '../hooks/supplier/interchanges/useSupplierInterchanges';
import { useSimpleTablePaginationHandlers } from '../hooks/pagination/useSimpleTablePaginationHandlers';
import { useSnackbarDisplayOnError } from '../hooks/error/useSnackbarDisplayOnError';
import useDebounce from '../hooks/useDebounce';
import { DateFormats } from '../components/constants/securspace-constants';
import { dialogTypes, InterchangeOpenDialog, dialogTypesKeys } from '../components/interchanges/data';
import moment, { MomentInput } from 'moment';
import { Grid, Typography, TablePagination } from '@mui/material';
import { styled } from '@mui/system';
import { SortDirType } from '../types/common/SortDirType';
import { InventoryActivityReportViewVO } from '../types/model/valueObject/InventoryActivityReportViewVO';

const InterchangesHeaderContainer = styled('div') ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
});

const CSVButtonParent = styled('div') ({
    marginTop: '2em',
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%'
})

const CSVButtonBox = styled('div') ({
    width: '20%'
})


interface InterchangeReportProps {
    account: any;
}

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 10;
const DEFAULT_START_DATE = moment().subtract(30, 'days').format('MM/DD/YYYY');
const DEFAULT_END_DATE = moment().format('MM/DD/YYYY');

const SupplierInterchangeReport = (props: InterchangeReportProps) => {
    const { snackbarShowMessage } = useSnackbar();
    const {account} = props;

    const defaultDialog: InterchangeOpenDialog = {open: false, type: dialogTypes.DELETE as dialogTypesKeys, action: () => {}};

      // Search params
    const [page, setPage] = useBindSearchParam('page', DEFAULT_PAGE.toString());
    const [size, setSize] = useBindSearchParam('size', DEFAULT_SIZE.toString());

    const [locationId, setLocationId] = useBindSearchParam<string>('locationId', '');
    const [buyerId, setBuyerId] = useBindSearchParam<string>('buyerId', '');
    const [startDate, setStartDate] = useBindSearchParam<string>('startDate', DEFAULT_START_DATE);
    const [endDate, setEndDate] = useBindSearchParam<string>('endDate', DEFAULT_END_DATE);
    const [equipmentNumber, setEquipmentNumber] = useBindSearchParam<string>('equipmentNumber', '');
    const [bookingNumber, setBookingNumber] = useBindSearchParam<string>('bookingNumber', '');
    const [activity, setActivity] = useBindSearchParam<string>('activity', '');
    const [assetType, setAssetType] = useBindSearchParam<string>('assetType', '');
    const [driverName, setDriverName] = useBindSearchParam<string>('driverName', '');
    const [sortBy, setSortBy] = useBindSearchParam<SupplierInterchangeSortByType>('sortBy', 'ACTIVITY_DATE');
    const [sortDir, setSortDir] = useBindSearchParam<SortDirType>('sortDir', 'desc');

    const [openDialog, setOpenDialog] = useState<InterchangeOpenDialog>(defaultDialog);
    const [interchangeBeingActioned, setInterchangeBeingActioned] = useState<InventoryActivityReportViewVO | null>();
    const [csvQuery, setCsvQuery] = useState({
                locationId: '',
                startDate: '',
                endDate: '',
                bookingNumber: '',
                equipmentNumber: '',
                assetType: '',
                buyerId: '',
                driverName: '',
                activity: ''
            });

    // debounced values so apis don't get spammed while typing
    const debouncedBooking = useDebounce(bookingNumber, 250);
    const debouncedEquipmentNumber = useDebounce(equipmentNumber, 250);
    const debouncedDriverName = useDebounce(driverName, 250);

    // api hooks
    const { interchanges, pageInfo, isLoading, error, doRequest } = useSupplierInterchanges({ page, size, locationId, buyerId, startDate, endDate, equipmentNumber: debouncedEquipmentNumber, booking: debouncedBooking, driverName: debouncedDriverName, activity, assetType, sortBy, sortDir });

    // error hooks
    useSnackbarDisplayOnError({error, snackbarShowMessage});

    // change handlers
    const handleStartDateChange = (value: MomentInput) => {
        if (value && moment(value).isValid()) {
            setStartDate(moment(value).format(DateFormats.DAY));
        } else {
            setStartDate('');
        }
    }

    const handleEndDateChange = (value: MomentInput) => {
        if (value && moment(value).isValid()) {
            setEndDate(moment(value).format(DateFormats.DAY));
        } else {
            setEndDate('');
        }
    }

    const {
    handlePageChange,
    handleRowsPerPageChange,
    } = useSimpleTablePaginationHandlers({ setPage, setSize });

    const handleCloseDialog = () => {
        setInterchangeBeingActioned(null);
        setOpenDialog(defaultDialog)
    };

    const moveInterchange = (id: string, orderNumber: string) => {
        moveInventory(id, orderNumber)
            .then(() => {
                snackbarShowMessage(`Successfully moved interchange to booking: ${orderNumber}`, 'success');
                handleCloseDialog();
                doRequest();
            })
            .catch(err => {
                const errorMessage = getErrorMessageForNonStandardAndStandardResponse(err);
                if (errorMessage.includes('Code: 11001')) {
                    snackbarShowMessage('The booking number you entered is invalid', 'error');
                } else {
                    snackbarShowMessage(errorMessage, 'error');
                }
            })

    }

    const confirmOrderNumber = (interchangeId: string, orderNumber: string) => {
        if(orderNumber) {
            confirmBookingNumber(orderNumber)
                .then(() => moveInterchange(interchangeId, orderNumber)
                ).catch((err: any) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'))
        }
    };

    const handleEditInterchange = (interchange: ActionInterchange) => {
        editInterchange(interchange)
            .then((res: any) => {
                snackbarShowMessage(`Successfully edited interchange: ${res?.data?.id}`, 'success');
                handleCloseDialog();
                doRequest();
            }).catch((err: any) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'))

    };

    const handleDeleteInterchange = (id: string) => {
        deleteInterchange(id)
            .then(() => {
                snackbarShowMessage(`Successfully deleted interchange`, 'success');
                handleCloseDialog();
                doRequest();
            }).catch((err: any) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'));
    };

    const handleOpenDialog = (type: keyof typeof dialogTypes, item: InventoryActivityReportViewVO) => {
        setInterchangeBeingActioned(item)

        let onAction;

        switch(type) {
            case 'EDIT':
                onAction = handleEditInterchange;
                break;
            case 'MOVE':
                onAction = confirmOrderNumber;
                break;
            case 'DELETE':
                onAction = handleDeleteInterchange;
                break;
            default:
                onAction = defaultDialog.action;
        }

        setOpenDialog({open: true, type: type, action: onAction});

    };

    useEffect(() => {
        setCsvQuery({locationId, startDate, endDate, bookingNumber, equipmentNumber, assetType, buyerId, driverName, activity})
    }, [locationId, bookingNumber, buyerId, startDate, endDate, equipmentNumber, driverName, activity, assetType]);

    return <SecurSpaceContainer>

        <SecurSpaceReportContainer>
            <Typography variant='h5' component='h1' mb={4}>Interchanges</Typography>
            <InterchangesHeaderContainer>
                <SupplierInterchangeFilters
                    startDate={startDate}
                    handleStartDateChange={handleStartDateChange}
                    endDate={endDate}
                    handleEndDateChange={handleEndDateChange}
                    locationId={locationId}
                    setLocationId={setLocationId}
                    buyerId={buyerId}
                    setBuyerId={setBuyerId}
                    equipmentNumber={equipmentNumber}
                    setEquipmentNumber={setEquipmentNumber}
                    bookingNumber={bookingNumber}
                    setBookingNumber={setBookingNumber}
                    assetType={assetType}
                    setAssetType={setAssetType}
                    driverName={driverName}
                    setDriverName={setDriverName}
                    activity={activity}
                    setActivity={setActivity}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDir={sortDir}
                    setSortDir={setSortDir}
                />
                <CSVButtonParent>
                    {
                        (interchanges && interchanges.length > 0) &&
                        <CSVButtonBox>
                            <DownloadCSVButton
                                className={'w-100'}
                                title='Download Report'
                                reportUrl={csvQuery ? getSupplierInterchangesReport(csvQuery) : ''}
                            />
                        </CSVButtonBox>
                    }
                </CSVButtonParent>
            </InterchangesHeaderContainer>
            <SecurSpaceReportPaginationContainer>
                <Grid width='100%'>
                    <SupplierInterchangeMap interchanges={interchanges} openDialog={handleOpenDialog} account={account} isLoading={isLoading}/>
                    <TablePagination
                        component={'div'}
                        count={pageInfo?.count || 0}
                        page={pageInfo?.page || DEFAULT_PAGE}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageInfo?.rowsPerPage || DEFAULT_SIZE}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </Grid>
            </SecurSpaceReportPaginationContainer>
            {
                interchangeBeingActioned &&
                <SupplierInterchangeDialogWrapper openDialog={openDialog} interchange={interchangeBeingActioned} onClose={handleCloseDialog} />
            }
        </SecurSpaceReportContainer>
    </SecurSpaceContainer>
};

export default SupplierInterchangeReport;
